import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import PricingModal from '../Pricing/PricingModal';
import { Button } from '@/components/ui/button';

interface CreditExhaustedModalProps {
  isOpen: boolean;
  onClose: () => void;
  renewalDate?: string | null;
  creditsInfo?: {
    usage_percent?: number;
    current_usage?: number;
    limit?: number;
    remaining?: number;
  };
}

export const CreditExhaustedModal: React.FC<CreditExhaustedModalProps> = ({
  isOpen,
  onClose,
  renewalDate,
  creditsInfo,
}) => {
  const [showPricingModal, setShowPricingModal] = React.useState(false);

  if (!isOpen) {
    return null;
  }

  const formatRenewalDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'data não disponível';
    
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch (error) {
      return dateString;
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        {/* Modal */}
        <div
          className="bg-background rounded-lg shadow-xl max-w-md w-full p-6 relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Fechar"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>

          {/* Content */}
          <div className="mt-2">
            <div className="mb-4">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-900/30 rounded-full">
                <svg
                  className="w-8 h-8 text-red-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-foreground text-center mb-2">
                Créditos Esgotados - Faça Upgrade
              </h2>
            </div>

            <div className="mb-6">
              <p className="text-muted-foreground text-center">
                Créditos esgotados, considere fazer um upgrade ou aguarde a renovação de créditos em{' '}
                <span className="font-semibold text-foreground">
                  {formatRenewalDate(renewalDate || undefined)}
                </span>
              </p>
            </div>

            {creditsInfo && (
              <div className="mb-6 p-4 bg-muted/50 rounded-lg">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Créditos utilizados:</span>
                    <span className="font-semibold text-foreground">
                      {creditsInfo.current_usage?.toFixed(1) || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Limite do plano:</span>
                    <span className="font-semibold text-foreground">
                      {creditsInfo.limit || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Uso:</span>
                    <span className="font-semibold text-red-400">
                      {creditsInfo.usage_percent?.toFixed(1) || 'N/A'}%
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => setShowPricingModal(true)}
                variant="hero"
                className="flex-1"
              >
                Fazer Upgrade
              </Button>
              <Button
                onClick={onClose}
                variant="outline"
                className="flex-1"
              >
                Fechar
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Modal */}
      <PricingModal
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        onSelectPlan={() => {
          setShowPricingModal(false);
          onClose();
        }}
      />
    </>
  );
};
